import { View } from 'react-native';

import { Button, Spinner, Text, VStack, theme } from '@squire/ui';

import { LegalAgreementFootnote } from '@/components/_shared/LegalAgreementFootnote';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { ConfirmBookingFooterProps } from './types';

const ConfirmBookingFooter = ({
  onBook,
  disabled = false,
  loading = false,
  onTermsPress,
  onPrivacyPress,
  isWaitlistMode = false,
}: ConfirmBookingFooterProps) => {
  const { t } = useScreenTranslations('ConfirmBookingBottomSheet');

  return (
    <VStack space={theme.spacing[3]}>
      <Button
        testID="confirm-booking-button"
        onPress={loading ? undefined : onBook}
        // Not `disabled` while loading: an HTML-disabled button drops out of
        // the tab order, so a keyboard user loses their place mid-checkout.
        disabled={disabled && !loading}
        aria-disabled={disabled || loading || undefined}
        aria-busy={loading || undefined}
        style={loading ? styles.loading : undefined}
        variant="primary"
        size="medium"
        accessibilityRole="button"
        accessibilityLabel={t('accessibility.bookButton')}
        accessibilityHint={t('accessibility.bookButtonHint')}
      >
        {loading ? (
          <View style={styles.loadingContent}>
            <Text color="textPrimaryInverse" variant="lStrong">
              {t('booking')}
            </Text>
            <Spinner color="textPrimaryInverse" size="small" />
          </View>
        ) : (
          <Text color="textPrimaryInverse" variant="lStrong">
            {isWaitlistMode ? t('joinWaitlist') : t('book')}
          </Text>
        )}
      </Button>

      <Text
        testID="confirm-booking-loading-status"
        aria-live="polite"
        style={styles.loadingStatus}
      >
        {loading ? t('accessibility.loading') : ''}
      </Text>
      <LegalAgreementFootnote
        introText={t('legalText')}
        termsLabel={t('termsOfService')}
        conjunctionText={t('and')}
        privacyLabel={t('privacyPolicy')}
        onTermsPress={onTermsPress}
        onPrivacyPress={onPrivacyPress}
      />
    </VStack>
  );
};

ConfirmBookingFooter.displayName = 'ConfirmBookingFooter';
export { ConfirmBookingFooter };
